import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { QRCodeCanvas } from "qrcode.react";
import { Download, RefreshCw, Loader2, Printer, CalendarDays, X } from "lucide-react";
import { format, parseISO } from "date-fns";
import { es, pt, enGB, fr } from "date-fns/locale";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Switch } from "@/components/ui/switch";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { downloadBlob, fmt } from "@/lib/demo/utils";
import { ORIGIN_SHORT } from "@/lib/i18n/dictInteracciones";
import { useI18n } from "@/lib/i18n/i18n";

const Panel = ({ title, children, testId }) => {
  const { t } = useI18n();
  return (
    <section className="rounded-[24px] border border-slate-200 bg-white p-6" data-testid={testId}>
      <h2 className="font-display text-2xl font-bold tracking-tight">{t(title)}</h2>
      {children}
    </section>
  );
};

const esc = (s) => String(s || "").replace(/[<>&"]/g, "");

export function printStandQr(box, c, t) {
  const img = box?.querySelector("canvas")?.toDataURL("image/png");
  const w = window.open("", "_blank");
  if (!w || !img) return;
  w.document.write(`<html><body style="font-family:sans-serif;text-align:center;padding:40px"><h1>${esc(c.name)}</h1><p>Stand ${esc(c.stand)}</p><img src="${img}" style="width:420px"/><p>${esc(t("Escanea para compartir tu CV"))}</p></body></html>`);
  w.document.close();
  w.print();
}

const LOCALES = { es, pt, en: enGB, fr };

const DateField = ({ label, value, onChange, testId }) => {
  const { t, lang } = useI18n();
  return (
    <div>
      <p className="truncate text-xs font-semibold uppercase tracking-wider text-slate-500" title={t(label)}>{t(label)}</p>
      <div className="mt-1 flex gap-1">
        <Popover>
          <PopoverTrigger asChild>
            <button type="button" className="flex h-11 flex-1 items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 text-left text-sm font-semibold" data-testid={testId}>
              <CalendarDays className="h-4 w-4 text-upv" /> {value ? format(parseISO(value), "dd/MM/yyyy") : <span className="font-normal text-slate-400">{t("Sin fecha")}</span>}
            </button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="start">
            <Calendar mode="single" locale={LOCALES[lang]} weekStartsOn={1} selected={value ? parseISO(value) : undefined} onSelect={(d) => onChange(d ? format(d, "yyyy-MM-dd") : null)} initialFocus />
          </PopoverContent>
        </Popover>
        {value && <button type="button" onClick={() => onChange(null)} title={t("Quitar fecha")} className="flex h-11 w-11 items-center justify-center rounded-xl border border-slate-200 text-slate-500 hover:text-slate-900" data-testid={`${testId}-clear`}><X className="h-4 w-4" /></button>}
      </div>
    </div>
  );
};

export const EditionSettings = () => {
  const { t } = useI18n();
  const { setSettings } = useAuth();
  const [s, setS] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/settings").then((r) => setS(r.data)).catch(() => {}); }, []);
  if (!s) return null;
  const set = (k) => (v) => setS((x) => ({ ...x, [k]: v }));
  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.put("/admin/settings", { name: s.name, optionA: s.optionA, optionB: s.optionB, postEvent: s.postEvent, postEventStart: s.postEventStart || null, postEventEnd: s.postEventEnd || null });
      setS(data); setSettings(data); toast.success(t("Configuración guardada"));
    } catch (e) { toast.error(t(apiError(e).message)); } finally { setBusy(false); }
  };
  const rows = [
    ["optionB", "Opción B · el candidato escanea el QR del stand (recomendado)"],
    ["optionA", "Opción A · la empresa escanea el QR del candidato"],
    ["postEvent", "Envío online de CV tras el Foro"],
  ];
  return (
    <Panel title="Configuración de la edición" testId="backoffice-settings">
      <p className="mt-1 text-sm text-slate-500">{s.name} · <span className="font-mono text-xs">{s.editionId}</span></p>
      <div className="mt-5 space-y-3">
        {rows.map(([k, l]) => (
          <label key={k} className="flex items-center justify-between gap-4 rounded-xl bg-slate-50 px-4 py-3">
            <span className="text-sm font-semibold">{t(l)}</span>
            <Switch checked={!!s[k]} onCheckedChange={set(k)} aria-label={t(l)} data-testid={`backoffice-settings-${k}`} />
          </label>
        ))}
      </div>
      <div className="mt-4 grid gap-3 sm:grid-cols-2">
        <DateField label="Inicio envío post-evento" value={s.postEventStart} onChange={set("postEventStart")} testId="backoffice-settings-start" />
        <DateField label="Fin envío post-evento" value={s.postEventEnd} onChange={set("postEventEnd")} testId="backoffice-settings-end" />
      </div>
      <p className="mt-3 text-xs text-slate-400">{t("Envío online ahora")}: <span className="font-semibold" data-testid="backoffice-settings-postevent-open">{t(s.postEventOpen ? "abierto" : "cerrado")}</span></p>
      <button onClick={save} disabled={busy} className="mt-5 flex h-12 w-full items-center justify-center gap-2 rounded-xl bg-slate-950 font-bold text-white disabled:opacity-60" data-testid="backoffice-settings-save">{busy && <Loader2 className="h-4 w-4 animate-spin" />}{t("Guardar configuración")}</button>
    </Panel>
  );
};

const QrTile = ({ c, onRegenerated }) => {
  const { t } = useI18n();
  const box = useRef(null);
  const url = `${window.location.origin}/q/${c.qrToken}`;
  const save = () => box.current?.querySelector("canvas")?.toBlob((b) => b && downloadBlob(b, `QR_stand_${c.slug}.png`));
  const [confirm, setConfirm] = useState(false);
  const print = () => printStandQr(box.current, c, t);
  const regen = async () => {
    setConfirm(false);
    try { const { data } = await api.post(`/admin/companies/${c.slug}/qr/regenerate`); onRegenerated(c.slug, data.qrToken); toast.success(t("QR regenerado")); } catch (e) { toast.error(t(apiError(e).message)); }
  };
  return (
    <li className="flex items-center gap-4 rounded-2xl bg-slate-50 p-4" data-testid={`backoffice-qr-${c.slug}`}>
      <div ref={box} className="w-20 shrink-0 rounded-xl bg-white p-1.5"><QRCodeCanvas value={url} size={512} level="M" marginSize={1} style={{ width: "100%", height: "auto" }} /></div>
      <div className="min-w-0 flex-1">
        <p className="truncate font-semibold">{c.name} <span className="text-xs text-slate-400">· {c.stand}</span></p>
        <p className="truncate font-mono text-[11px] text-slate-500">{c.qrToken}</p>
        <p className="mt-1 text-[11px] text-slate-500">{fmt(c.total)} · {Object.entries(ORIGIN_SHORT).map(([o, l]) => `${t(l)} ${fmt(c.byOrigin[o] || 0)}`).join(" · ")}</p>
      </div>
      <div className="flex shrink-0 gap-1">
        <button onClick={save} title={t("Descargar QR")} className="rounded-lg p-2 text-slate-600 hover:bg-white" data-testid={`backoffice-qr-download-${c.slug}`}><Download className="h-4 w-4" /></button>
        <button onClick={print} title={t("Imprimir")} className="rounded-lg p-2 text-slate-600 hover:bg-white" data-testid={`backoffice-qr-print-${c.slug}`}><Printer className="h-4 w-4" /></button>
        <button onClick={() => setConfirm(true)} title={t("Regenerar QR")} className="rounded-lg p-2 text-slate-600 hover:bg-white" data-testid={`backoffice-qr-regenerate-${c.slug}`}><RefreshCw className="h-4 w-4" /></button>
      </div>
      <AlertDialog open={confirm} onOpenChange={setConfirm}>
        <AlertDialogContent className="rounded-[28px]" data-testid={`backoffice-qr-regenerate-dialog-${c.slug}`}>
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display text-2xl font-bold">{t("¿Regenerar el QR del stand?")}</AlertDialogTitle>
            <AlertDialogDescription>{c.name} · {t("El QR actual dejará de funcionar. ¿Continuar?")}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid={`backoffice-qr-regenerate-cancel-${c.slug}`}>{t("Cancelar")}</AlertDialogCancel>
            <AlertDialogAction onClick={regen} className="bg-upv hover:bg-upv-dark" data-testid={`backoffice-qr-regenerate-confirm-${c.slug}`}>{t("Regenerar")}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </li>
  );
};

export const CompanyQrList = () => {
  const [items, setItems] = useState(null);
  useEffect(() => { api.get("/admin/companies").then((r) => setItems(r.data.items)).catch(() => setItems([])); }, []);
  const onRegenerated = (slug, qrToken) => setItems((xs) => xs.map((x) => (x.slug === slug ? { ...x, qrToken } : x)));
  return (
    <Panel title="QR de empresas" testId="backoffice-company-qrs">
      {!items ? <Loader2 className="mt-4 h-5 w-5 animate-spin text-slate-400" /> : (
        <ul className="mt-4 max-h-[560px] space-y-2 overflow-y-auto">{items.map((c) => <QrTile key={c.slug} c={c} onRegenerated={onRegenerated} />)}</ul>
      )}
    </Panel>
  );
};
